<template>
  <nav
    :class="[
      'mainNav',
      $style.nav,
      {[$style.navExpanded]: !collapsed},
      {[$style.productTourActive]: showProductTour},
    ]"
  >
    <header
      :class="$style.navPanelHeader"
    >
      <a
        :class="$style.logoLink"
        :href="logoLink.href"
        title="JumpCloud Directory Platform"
        @click="trackNavClicked(logoLink.href, logoLink.title)"
      >
        <JumpcloudBrandmark
          darkBg
          :scale="1.75"
          :type="collapsedLogo"
        />
      </a>
    </header>
    <div
      ref="navPanelBody"
      :class="$style.navPanelBody"
    >
      <div :class="[$style.navPanelSection, $style.gettingStartedSection]">
        <ul :class="$style.linkList">
          <li :class="$style.linkListItem">
            <DiscoverLink
              v-if="filterLinks(discoverLink)"
              :activeLink="activeLink"
              :isActive="isActive(discoverLink.href)"
              :item="discoverLink"
              :panelExpanded="!collapsed"
            />
            <HomeLink
              v-if="filterLinks(homeLink)"
              :activeLink="activeLink"
              :isActive="isActive(homeLink.href)"
              :item="homeLink"
              :panelExpanded="!collapsed"
            />
          </li>
        </ul>
      </div>
      <NavPanelSection
        v-for="navPanelSection in navPanelSections"
        :key="navPanelSection.title"
        :activeLink="activeLink"
        :class="$style.navPanelSection"
        :isActive="isActive"
        :links="navPanelSection.links"
        :panelExpanded="!collapsed"
        :title="navPanelSection.title"
      >
        <template
          v-if="navPanelSection.announcementPopoverComponent"
          #popover
        >
          <SSOMovedAnnouncement
            :sectionCollapsed="collapsed"
          />
        </template>
      </NavPanelSection>
    </div>
    <footer :class="$style.navFooter">
      <ul :class="$style.linkList">
        <li
          v-if="!isCommandRunner && isSimplifiedOnboardingDismissed
            && !$hasFeatureFlag($featureFlags.intercomSupport)"
          :class="$style.linkListItem"
        >
          <Chat
            :expanded="!collapsed"
            :imageComponent="chatLink.imageComponent"
          />
        </li>
        <li
          v-if="$hasFeatureFlag($featureFlags.intercomSupport)"
          :class="$style.linkListItem"
        >
          <IntercomChat
            :expanded="!collapsed"
            :imageComponent="chatLink.imageComponent"
          />
        </li>
        <li
          v-if="!isCommandRunner"
          :class="$style.linkListItem"
        >
          <NavPanelLink
            :active="isActive(settingsLink.href)"
            :activeLink="activeLink"
            data-test-id="NavPanel__settingsLink"
            :highlightProductTour="showProductTourPopover"
            :item="settingsLink"
            :panelExpanded="!collapsed"
          >
            <template
              v-if="showProductTourPopover"
              v-slot:popover
            >
              <NavPanelProductTour
                :expanded="!collapsed"
              />
            </template>
          </NavPanelLink>
        </li>
        <li
          v-if="showAccountLink"
          :class="$style.linkListItem"
        >
          <NavPanelLink
            :active="isActive(accountLink.href)"
            :activeLink="activeLink"
            data-test-id="NavPanel__accountLink"
            :item="accountLink"
            :panelExpanded="!collapsed"
          />
        </li>
        <li :class="[$style.linkListItem, $style.collapseItem]">
          <NavPanelLink
            data-test-id="navPanel__collapseNavButton"
            isButton
            :item="collapseItem"
            :panelExpanded="!collapsed"
            secondaryColor
            @click="handleToggleClick"
          />
        </li>
      </ul>
    </footer>
  </nav>
</template>
<script>
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  LocalStorageConsts,
  LoginModalConsts,
} from 'client/util/Constants';
import { JumpcloudBrandmark } from '@jumpcloud/ui-components';
import { mapGetters, mapState } from 'vuex';

import Chat from 'client/GrowthEngineering/components/Chat.vue';
import Cookies from 'js-cookie';
import DiscoverLink from 'client/components/NavPanel/DiscoverLink.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import HomeLink from 'client/components/NavPanel/HomeLink.vue';
import IconCollapse from 'public/img/icons/nav/icon-nav-collapse.svg?inline';
import IntercomChat from 'client/GrowthEngineering/components/IntercomChat.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import LoggingService from 'client/util/LoggingService';
import NavPanelLink from 'client/components/NavPanel/NavPanelLink.vue';
import NavPanelLinkList from 'client/components/NavPanel/NavPanelLinkList';
import NavPanelLinkListItem from 'client/components/NavPanel/NavPanelLinkListItem';
import NavPanelProductTour from 'client/components/NavPanel/NavPanelProductTour';
import NavPanelSection from 'client/components/NavPanel/NavPanelSection.vue';
import OptimizelyHelper from 'client/GrowthEngineering/OptimizelyHelper';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import SSOMovedAnnouncement from 'client/applications/SSOMovedAnnouncement.vue';

import iconCommands from 'public/img/icons/nav/icon-nav-commands.svg?inline';

const {
  accountLink,
  activeDirectoryLink,
  applicationsLink,
  chatLink,
  cloudDirectoriesLink,
  cloudInsightsLink,
  conditionalListsLink,
  conditionalPoliciesLink,
  configurationsLink,
  configurationGroupsLink,
  deviceGroupsLink,
  devicesLink,
  directoryInsightsLink,
  discoverLink,
  homeLink,
  hrDirectoriesLink,
  ldapLink,
  mfaLink,
  mdmLegacyLink,
  mdmLink,
  passwordManagerLink,
  pkiConsole,
  softwareAppsLink,
  radiusLink,
  reportsLink,
  settingsLink,
  ssoLink,
  userGroupsLink,
  usersLink,
} = NavPanelLinkList;

const { excludeModalTestingCookie } = LoginModalConsts;
const { navPanelCollapsed, navPanelSectionsCollapsed } = LocalStorageConsts;

const onboardingModule = 'OnboardingModule';
const partnersModule = 'PartnersModule';
const settingsModule = 'SettingsModule';

export default {
  name: 'NavPanel',

  components: {
    Chat,
    DiscoverLink,
    HomeLink,
    IntercomChat,
    JumpcloudBrandmark,
    NavPanelLink,
    NavPanelProductTour,
    NavPanelSection,
    SSOMovedAnnouncement,
  },

  LocalStorageService,

  props: {
    // Used by legacy backbone router. Set by Viewport.renderMainPanel()
    // Vue sets this data via watch.$route
    activeLink: {
      type: String,
      default: '',
    },
    hasBilling: {
      type: Boolean,
      required: false,
    },
    isCommandRunner: {
      type: Boolean,
      required: true,
    },
    isProvider: {
      type: Boolean,
      required: false,
    },
    isProviderOrg: Boolean,
    ssoNavBarExperimentActive: Boolean,
  },

  data: () => ({
    accountLink,
    chatLink,
    discoverLink,
    collapsed: false,
    homeLink,
    settingsLink,
  }),

  computed: {
    ...mapState(onboardingModule, [
      'adminGrowthData',
      'showProductTour',
    ]),

    ...mapGetters(onboardingModule, [
      'getProductTourActiveLink',
    ]),

    ...mapGetters('FeatureFlagsModule', ['betaFeatures']),

    ...mapGetters(partnersModule, [
      'hasActiveCrowdStrikeLink',
    ]),

    ...mapGetters(settingsModule, {
      user: 'getUser',
    }),
    ...mapGetters(settingsModule, [
      'isAdminWithBilling',
    ]),

    collapseItem() {
      return new NavPanelLinkListItem({
        imageComponent: IconCollapse,
        title: this.collapsedLabel,
      });
    },

    collapsedLabel() {
      const collapsedLabel = this.collapsed ? 'Expand' : 'Collapse';
      return `${collapsedLabel} Menu`;
    },

    collapsedLogo() {
      return this.collapsed ? 'jcLogo' : 'jcLogoWordmark';
    },

    deviceManagementLinks() {
      const commandsLink = new NavPanelLinkListItem({
        title: 'Commands',
        href: ItemTypeRoutes[ItemTypeConsts.command],
        imageComponent: iconCommands,
        showForCommandRunners: true,
        isNew: this.isProvider && this.isAdminWithBilling,
      });
      return [
        devicesLink,
        deviceGroupsLink,
        configurationsLink,
        configurationGroupsLink,
        commandsLink,
        /**
         * following line shows different icon depending upon
         * "windowsMdmEnrollment" or "androidEmmMvp" flag
         */
        this.$hasFeatureFlag(this.$featureFlags.windowsMdmEnrollment)
        || this.$hasFeatureFlag(this.$featureFlags.androidEmmMvp) ? mdmLink : mdmLegacyLink,
        softwareAppsLink,
      ];
    },

    directoryIntegrationLinks() {
      const directoryIntegrationLinks = [
        activeDirectoryLink,
        cloudDirectoriesLink,
        hrDirectoriesLink,
      ];

      if (this.ssoNavBarExperimentActive) {
        directoryIntegrationLinks.unshift(applicationsLink);
      }

      return directoryIntegrationLinks;
    },

    insightsLinks() {
      return [
        directoryInsightsLink,
        cloudInsightsLink,
        reportsLink,
      ];
    },

    isSimplifiedOnboardingDismissed() {
      return this.adminGrowthData.onboardingState.simplifiedOnboardingDismissed
      || !!Cookies.get(excludeModalTestingCookie);
    },

    logoLink() {
      let link = usersLink;

      if (OptimizelyHelper.shouldShowHomeAsLandingPage()) {
        link = homeLink;
      }

      return link;
    },

    navPanelSections() {
      const sections = [
        {
          links: this.userManagementLinks,
          title: 'User Management',
        },
        {
          links: this.userAuthenticationLinks,
          title: 'User Authentication',
        },
        {
          links: this.deviceManagementLinks,
          title: 'Device Management',
        },
        {
          links: this.securityManagementLinks,
          title: 'Security Management',
        },
        {
          links: this.insightsLinks,
          title: 'Insights',
        },
      ];

      const integrationsMenuSection = {
        links: this.directoryIntegrationLinks,
      };

      if (this.ssoNavBarExperimentActive) {
        integrationsMenuSection.title = 'Integrations';
        integrationsMenuSection.announcementPopoverComponent = true;
      } else {
        integrationsMenuSection.title = 'Directory Integrations';
      }

      sections.splice(3, 0, integrationsMenuSection);

      // remove links hidden by Feature Flags then empty sections.
      return sections
        .map(section => ({
          ...section,
          links: section.links.filter(this.filterLinks),
        }))
        .filter(section => section.links.length > 0);
    },

    securityManagementLinks() {
      return [
        pkiConsole,
        conditionalPoliciesLink,
        conditionalListsLink,
        mfaLink,
      ];
    },

    showAccountLink() {
      return this.hasBilling
        // is Provider checks the provider user (i.e. the head MTP admin)
        && !this.isProvider
        && !this.isProviderOrg
        && this.filterLinksByFeatureFlag(this.accountLink);
    },

    showProductTourPopover() {
      return this.showProductTour && this.settingsLink === this.getProductTourActiveLink;
    },

    userAuthenticationLinks() {
      const userAuthenticatonLinks = [
        ldapLink,
        radiusLink,
        passwordManagerLink,
      ];

      if (!this.ssoNavBarExperimentActive) {
        userAuthenticatonLinks.splice(2, 0, ssoLink);
      }

      return userAuthenticatonLinks;
    },

    userManagementLinks() {
      return [
        usersLink,
        userGroupsLink,
      ];
    },
  },

  watch: {
    showProductTour(newValue) {
      if (newValue) {
        this.collapsed = false;
      } else {
        this.collapsed = this.$options.LocalStorageService.getValue(navPanelCollapsed);
      }
    },
  },

  mounted() {
    this.updateCollapse();
    this.handlePartnersSectionCollapse();
  },

  methods: {
    filterLinks(link) {
      return this.filterLinksByFeatureFlag(link) && this.filterLinksForCommandRunners(link);
    },

    filterLinksByFeatureFlag(link) {
      if (link.featureFlag != null) {
        // the bang flag is a quick solution for adding the new tag to an existing link
        // there is probably a better long-term solution if we continue to tag existing links
        const isFlaggedForRemoval = link.featureFlag.startsWith('!');
        return isFlaggedForRemoval
          ? !this.betaFeatures[link.featureFlag.slice(1)]
          : !!this.betaFeatures[link.featureFlag];
      }
      return true;
    },

    filterLinksForCommandRunners(link) {
      if (this.isCommandRunner) {
        return link.showForCommandRunners || false;
      }

      return true;
    },

    handlePartnersSectionCollapse() {
      if (this.user.sessionCount === 1 && this.hasActiveCrowdStrikeLink) {
        const navPanelSectionCollapseState = {};
        this.navPanelSections.forEach((section) => {
          if (section.title === 'Device Management') {
            navPanelSectionCollapseState[section.title] = false;
          } else {
            navPanelSectionCollapseState[section.title] = true;
          }
        });

        this.$options.LocalStorageService.setItem(
          navPanelSectionsCollapsed, JSON.stringify(navPanelSectionCollapseState),
        );
      }
    },

    handleToggleClick() {
      this.collapsed = !this.collapsed;

      EventHelper.triggerNavPanelCollapsed(
        { action: this.collapsed ? 'Panel Collapsed' : 'Panel Expanded' },
      );

      this.$options.LocalStorageService.setItem(navPanelCollapsed, this.collapsed);
      this.$emit('navPanelCollapsed', this.collapsed);
    },

    isActive(href) {
      if (href) {
        const link = this.activeLink;

        if (href.includes(link) && link !== '') {
          LoggingService.debug('NavPanel.isActive: href includes link: ', { link, href });

          return true;
        }
        if (PageNavigationUtil.getLocationHash().includes(href)) {
          LoggingService.debug('NavPanel.isActive: locationHash includes href: ', {
            link, href, locationHash: PageNavigationUtil.getLocationHash(),
          });

          return true;
        }
      }
      return false;
    },

    trackNavClicked(href, toTitle) {
      const toHref = href.replace('#/', '');
      EventHelper.triggerNavItemClicked({
        fromHref: this.activeLink,
        logoClicked: true,
        toHref,
        toTitle,
      });
    },

    updateCollapse() {
      this.collapsed = this.$options.LocalStorageService.getValue(navPanelCollapsed);
    },
  },
};
</script>
<style module>
@import './css/NavPanel.css';

@value (max-width-large) from '~css/breakpoints.css';
@value (navWidth, navWidthExpanded) from '~css/layout.css';
@value (tooltipOverlayZIndex) from '~css/z-index.css';

.nav.navExpanded {
  width: navWidthExpanded;
}

.logo :global(.jc-wordmark) {
  opacity: 0;
  transition: all var(--jc-transition-duration) var(--jc-transition-function);
  width: 0;
}

.navExpanded :global(.jc-wordmark) {
  opacity: 1;
  width: auto;
}

.navPanelSection {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  padding-bottom: 20px;
}

.gettingStartedSection {
  padding-bottom: 0;
}

.collapseItem {
  bottom: 0;
}

.collapseItem svg {
  transform: rotate(180deg);
  transition: all var(--jc-transition-duration) var(--jc-transition-function);
}

.navExpanded .collapseItem svg {
  transform: none;
}

.productTourActive {
  z-index: tooltipOverlayZIndex;
}

@media (max-width: max-width-large) {
  .nav.navExpanded,
  .navExpanded {
    width: navWidth;
  }

  .navExpanded :global(.jc-wordmark) {
    margin-left: 0;
    opacity: 0;
    width: 0;
  }

  .collapseItem {
    display: none;
  }
}
</style>
